// Long-Press oeffnet einen Sortiermodus. Anders als reorder.ts (HTML5-
// DnD, nur Maus, persistenter Anordnen-Knopf) gibt es hier keinen Knopf:
//
//   - Ausserhalb des Modus: kurzer Klick/Tap navigiert normal, langes
//     Druecken (~450 ms ruhig gehalten) OEFFNET den Sortiermodus und hebt
//     die gedrueckte Karte direkt zum Ziehen an.
//   - Im Sortiermodus (armed=true): jede Karte laesst sich sofort per
//     kurzem Greifen-und-Ziehen umordnen; ein Tap navigiert NICHT mehr.
//     Den Modus beendet der Aufrufer (Klick irgendwo ausserhalb).
//
// Funktioniert mit Maus UND Touch (Pointer Events). Jede ziehbare Karte
// braucht data-reorder-id + data-reorder-group, damit das Ziel unter dem
// Finger gefunden werden kann. onReorder wird live waehrend der Bewegung
// gerufen (Einfuege-Modell), aber erst, wenn der Zeiger die MITTE des
// Ziels ueberquert: sonst sprangen ungleich hohe Karten hin und her --
// die grosse rutscht unter den Finger, der Finger steht wieder auf ihr,
// sie tauscht zurueck (16.10.2026, Seiten-Karten).
//
// Steht der Zeiger beim Ziehen am Rand der scrollenden Flaeche, scrollt
// sie mit. Das Scrollen per Finger ist waehrenddessen gesperrt, ohne das
// kaeme eine Karte nie weiter als bis zum Bildschirmrand.

// Modul-globaler Drag-Zustand: nur eine Karte gleichzeitig.
let active: { group: string; id: string } | null = null;

export interface PressReorderParams {
  group: string;
  id: string;
  onReorder: (group: string, fromID: string, toID: string) => void;
  // Ist der Sortiermodus aktiv? Dann ohne Long-Press direkt ziehbar.
  armed: boolean;
  // Long-Press hat den Sortiermodus geoeffnet (nur relevant wenn !armed).
  onActivate: () => void;
  // Wartezeit bis der Long-Press oeffnet. Default 450 ms.
  longPressMs?: number;
  // Bewegung, ab der wir aus dem Druck einen Drag/Scroll machen. Default 8.
  moveThresholdPx?: number;
  // CSS-Klassen auf die angehobene Karte (space-separated).
  liftClass?: string;
  // Nur ein Druck in DIESEM Teil der Karte (CSS-Selektor) oeffnet den
  // Modus und zieht -- etwa die Titelzeile einer Sektion, deren Inhalt
  // eigene Gesten hat (Tabellen-Spalten ziehen, Zeilen klicken, 3D-Modell
  // drehen). Ohne Angabe: die ganze Karte.
  griff?: string;
  // Gerade nicht sortierbar (z.B. die einzige Karte ihrer Gruppe): kein
  // Long-Press, Klicks laufen normal.
  aus?: boolean;
}

// Wer in ein Feld drueckt, will tippen, markieren oder einen Regler
// halten -- nie sortieren.
const FELD = "input, textarea, select, [contenteditable=''], [contenteditable='true']";

type Phase = "idle" | "longpress" | "armed" | "dragging";

export function pressReorder(node: HTMLElement, params: PressReorderParams) {
  let p = params;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let startX = 0;
  let startY = 0;
  let phase: Phase = "idle";

  const liftClasses = () => (p.liftClass ?? "").split(/\s+/).filter(Boolean);
  const threshold = () => p.moveThresholdPx ?? 8;

  function clearTimer() {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  }

  // Im Sortiermodus muss der Browser die Touch-Geste UNS ueberlassen, sonst
  // kapert das Scrollen sie und feuert pointercancel, bevor der Drag starten
  // kann (iOS-PWA: "oranger Rahmen kommt, aber Karte laesst sich nicht
  // ziehen"). touch-action:none nur waehrend armed -- sonst bleibt Scrollen.
  function applyArmed() {
    node.style.touchAction = p.armed ? "none" : "";
  }

  // ── Druck beobachten ───────────────────────────────────────────────

  function onPointerDown(e: PointerEvent) {
    if (e.button > 0) return; // nur primaerer Button / erster Finger
    // Overlays (Sheets/Modals) haengen im DOM INNERHALB der Karte; ihre
    // Gesten duerfen den Sortiermodus nicht oeffnen. stopPropagation im
    // Overlay reicht nicht -- Svelte delegiert pointerdown an die Wurzel,
    // DIESER Listener ist nativ und feuert vorher. Deshalb Opt-out per
    // data-no-reorder am Overlay-Root.
    if (p.aus) return;
    const ziel = e.target as HTMLElement;
    if (ziel.closest("[data-no-reorder]") || ziel.closest(FELD)) return;
    // Verschachtelte Scopes: startet der Druck auf einem TIEFER liegenden
    // Reorder-Element (eigene Gruppe INNERHALB dieser Karte), ueberlassen
    // wir die Geste dem inneren Handler. stopPropagation reicht dafuer
    // NICHT (s.o.) -- deshalb hier explizit am naechsten Reorder-Vorfahren
    // pruefen.
    const inner = ziel.closest<HTMLElement>("[data-reorder-id]");
    if (inner && inner !== node) return;
    if (p.griff) {
      const g = ziel.closest(p.griff);
      if (!g || !node.contains(g)) return;
    }
    startX = e.clientX;
    startY = e.clientY;
    if (p.armed) {
      phase = "armed"; // wartet auf Bewegung -> Drag, oder Up -> Tap
    } else {
      phase = "longpress";
      timer = setTimeout(openMode, p.longPressMs ?? 450);
    }
    node.addEventListener("pointermove", onPressMove);
    node.addEventListener("pointerup", onPressUp);
    node.addEventListener("pointercancel", onPressCancel);
  }

  function moved(e: PointerEvent): boolean {
    return Math.hypot(e.clientX - startX, e.clientY - startY) > threshold();
  }

  function onPressMove(e: PointerEvent) {
    if (phase === "longpress") {
      // Bewegung vor dem Oeffnen -> Scroll/Swipe, Long-Press abbrechen.
      if (moved(e)) {
        clearTimer();
        teardownPress();
        phase = "idle";
      }
    } else if (phase === "armed") {
      // Im Sortiermodus startet Bewegung sofort das Ziehen.
      if (moved(e)) beginDrag();
    }
  }

  function onPressUp() {
    clearTimer();
    teardownPress();
    // Tap im Sortiermodus -> Navigation schlucken (Karte ist Drag-Ziel,
    // kein Link). Tap ausserhalb des Modus -> normaler Klick navigiert.
    if (phase === "armed") swallowNextClick();
    phase = "idle";
  }

  function onPressCancel() {
    clearTimer();
    teardownPress();
    phase = "idle";
  }

  function teardownPress() {
    node.removeEventListener("pointermove", onPressMove);
    node.removeEventListener("pointerup", onPressUp);
    node.removeEventListener("pointercancel", onPressCancel);
  }

  // Long-Press abgelaufen -> Modus oeffnen + diese Karte gleich anheben.
  function openMode() {
    timer = undefined;
    teardownPress();
    p.onActivate();
    beginDrag();
  }

  // ── Ziehen ─────────────────────────────────────────────────────────

  function beginDrag() {
    teardownPress();
    phase = "dragging";
    active = { group: p.group, id: p.id };

    const cls = liftClasses();
    if (cls.length) node.classList.add(...cls);
    // pointer-events aus, damit elementFromPoint die Karte DARUNTER liefert.
    node.style.pointerEvents = "none";
    document.body.style.userSelect = "none";

    document.addEventListener("pointermove", onDragMove);
    document.addEventListener("pointerup", onDragEnd);
    document.addEventListener("pointercancel", onDragEnd);
    document.addEventListener("touchmove", preventScroll, { passive: false });
    scroller = scrollFlaeche(node);
    frame = requestAnimationFrame(randScroll);
  }

  // ── Mitscrollen am Rand ────────────────────────────────────────────

  let scroller: HTMLElement | null = null;
  let frame = 0;
  let zeiger: { x: number; y: number } | null = null;

  function randScroll() {
    frame = 0;
    if (phase !== "dragging") return;
    if (scroller && zeiger) {
      const r = scroller === document.scrollingElement
        ? { top: 0, bottom: window.innerHeight }
        : scroller.getBoundingClientRect();
      const rand = 56;
      const oben = zeiger.y - r.top;
      const unten = r.bottom - zeiger.y;
      // Je naeher am Rand, desto schneller, hoechstens 14 px je Bild.
      const schritt = oben < rand ? -Math.ceil(((rand - oben) / rand) * 14) : unten < rand ? Math.ceil(((rand - unten) / rand) * 14) : 0;
      if (schritt !== 0) {
        const vorher = scroller.scrollTop;
        scroller.scrollTop += schritt;
        // Unter dem ruhenden Finger zieht jetzt eine andere Karte durch.
        if (scroller.scrollTop !== vorher) treffe(zeiger.x, zeiger.y);
      }
    }
    frame = requestAnimationFrame(randScroll);
  }

  function preventScroll(e: TouchEvent) {
    e.preventDefault();
  }

  function onDragMove(e: PointerEvent) {
    zeiger = { x: e.clientX, y: e.clientY };
    treffe(e.clientX, e.clientY);
  }

  function treffe(x: number, y: number) {
    if (!active) return;
    const el = document.elementFromPoint(x, y);
    if (!el) return;
    // Nach dem NAECHSTEN Vorfahren der AKTIVEN Gruppe suchen -- so
    // kollidieren verschachtelte Reorder-Scopes nicht: eine Karte darueber
    // darf nicht die Zeilen einer anderen Gruppe als Ziel greifen.
    const target = (el as HTMLElement).closest<HTMLElement>(
      `[data-reorder-id][data-reorder-group="${active.group}"]`,
    );
    if (!target) return;
    const targetID = target.dataset.reorderId;
    if (!targetID || targetID === active.id) return;
    if (!ueberMitte(target.getBoundingClientRect(), node.getBoundingClientRect(), x, y)) return;
    p.onReorder(active.group, active.id, targetID);
  }

  function onDragEnd() {
    document.removeEventListener("pointermove", onDragMove);
    document.removeEventListener("pointerup", onDragEnd);
    document.removeEventListener("pointercancel", onDragEnd);
    document.removeEventListener("touchmove", preventScroll);
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    zeiger = null;
    scroller = null;

    const cls = liftClasses();
    if (cls.length) node.classList.remove(...cls);
    node.style.pointerEvents = "";
    document.body.style.userSelect = "";
    active = null;
    phase = "idle";
    swallowNextClick();
  }

  // Den Klick, der direkt nach dem pointerup feuert, schlucken -- sonst
  // navigiert das <a> nach Long-Press/Drag/Tap-im-Modus.
  function swallowNextClick() {
    node.addEventListener("click", swallowClick, true);
    setTimeout(() => node.removeEventListener("click", swallowClick, true), 0);
  }

  function swallowClick(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    node.removeEventListener("click", swallowClick, true);
  }

  // Natives Ziehen (Links, Bilder) wuerde die Geste kapern. Mit Griff
  // nur dort: im Inhalt einer Sektion ziehen Tabellen ihre Spalten per
  // HTML5-DnD, und das dragstart steigt bis hierher auf.
  function preventNativeDrag(e: DragEvent) {
    if (p.griff && !(e.target as Element | null)?.closest?.(p.griff)) return;
    e.preventDefault();
  }

  function syncDataset() {
    // Ziel-Erkennung unter dem Finger laeuft ueber diese Attribute --
    // die Action setzt sie selbst, damit jede Karte nur use:pressReorder
    // braucht (kein doppeltes data-* im Markup).
    node.dataset.reorderId = p.id;
    node.dataset.reorderGroup = p.group;
  }

  node.draggable = false;
  syncDataset();
  applyArmed();
  node.addEventListener("pointerdown", onPointerDown);
  node.addEventListener("dragstart", preventNativeDrag);

  return {
    update(next: PressReorderParams) {
      p = next;
      syncDataset();
      applyArmed();
    },
    destroy() {
      clearTimer();
      teardownPress();
      if (active && active.id === p.id) onDragEnd();
      node.removeEventListener("pointerdown", onPointerDown);
      node.removeEventListener("dragstart", preventNativeDrag);
    },
  };
}

// Liegt der Zeiger jenseits der Mitte des Ziels -- von der gezogenen Karte
// aus gesehen? Stehen beide in einer Zeile (Raster), zaehlt die
// Waagerechte, sonst die Senkrechte.
function ueberMitte(ziel: DOMRect, selbst: DOMRect, x: number, y: number): boolean {
  const zeile = Math.abs(ziel.top - selbst.top) < Math.min(ziel.height, selbst.height) / 2;
  if (zeile) {
    const mitte = ziel.left + ziel.width / 2;
    return selbst.left < ziel.left ? x > mitte : x < mitte;
  }
  const mitte = ziel.top + ziel.height / 2;
  return selbst.top < ziel.top ? y > mitte : y < mitte;
}

// Die naechste Flaeche, die senkrecht scrollt (Shell: die Editorflaeche,
// Telefon: <main>), sonst das Dokument.
export function scrollFlaeche(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) return p;
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
}
